import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CogIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  SparklesIcon,
  ScaleIcon,
  AdjustmentsHorizontalIcon,
  ClockIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { ConfigWizard } from './ConfigWizard';
import { CategoryCard } from './CategoryCard';

interface PortfolioConfigCardsProps {
  portfolioCategories: Record<string, string> | undefined;
  onValueChange: (path: string, value: any) => void;
  onRemoveOverride: (path: string) => void;
  onResetCategory: (category: string) => void;
  isLoadingCategories: boolean;
  isUpdating?: boolean;
}

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  lifecycle: ShieldCheckIcon,
  account_moves: ChartBarIcon,
  stability: ScaleIcon,
  scoring: AdjustmentsHorizontalIcon,
  allocation: PlusIcon,
  timing: ClockIcon,
};

export const PortfolioConfigCards: React.FC<PortfolioConfigCardsProps> = ({
  portfolioCategories,
  onValueChange,
  onRemoveOverride,
  onResetCategory,
  isLoadingCategories,
  isUpdating = false
}) => {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  const toggleCard = (categoryId: string) => {
    const newExpanded = new Set<string>();
    if (expandedCards.has(categoryId)) {
      // If clicking the same card, collapse it
      // Leave newExpanded empty to collapse all
    } else {
      // If clicking a different card, expand only that one
      newExpanded.add(categoryId);
    }
    setExpandedCards(newExpanded);
  };

  const handleWizardComplete = (configs: any) => {
    // Apply the wizard configurations
    Object.entries(configs).forEach(([path, value]) => {
      onValueChange(path, value);
    });
    setIsWizardOpen(false);
  };

  if (isLoadingCategories) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-6 animate-pulse"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-5 bg-white/20 rounded mb-2"></div>
                  <div className="h-4 bg-white/10 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Quick Setup Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-white/90">Portfolio Configuration</h3>
          <p className="text-white/70 text-sm mt-1">
            Customize your portfolio management settings by category
          </p>
        </div>
        
        <motion.button
          onClick={() => setIsWizardOpen(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 border border-white/20"
        >
          <SparklesIcon className="w-4 h-4 mr-2" />
          Quick Setup Wizard
        </motion.button>
      </div>

      {/* Configuration Cards Grid */}
      <div className="space-y-6">
        {/* Show navigation breadcrumbs when a card is expanded */}
        {expandedCards.size > 0 && portfolioCategories && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center space-x-2 py-4 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10"
          >
            <span className="text-sm text-white/60">Navigate:</span>
            {Object.entries(portfolioCategories).map(([categoryKey, _], index) => {
              const categoryName = categoryKey.replace('_', ' ').split(' ').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
              const isActive = expandedCards.has(categoryKey);
              
              return (
                <React.Fragment key={categoryKey}>
                  <button
                    onClick={() => toggleCard(categoryKey)}
                    className={`px-3 py-1 rounded-md text-sm transition-colors ${
                      isActive 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'text-white/70 hover:text-white/90 hover:bg-white/10'
                    }`}
                  >
                    {categoryName}
                  </button>
                  {index < Object.keys(portfolioCategories).length - 1 && (
                    <span className="text-white/30">•</span>
                  )}
                </React.Fragment>
              );
            })}
          </motion.div>
        )}

        {portfolioCategories && Object.entries(portfolioCategories).map(([categoryKey, description]) => {
          const IconComponent = categoryIcons[categoryKey] || CogIcon;
          const isExpanded = expandedCards.has(categoryKey);

          const categoryName = categoryKey.replace('_', ' ').split(' ').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');

          // If this card is expanded, render it full-width
          if (isExpanded) {
            return (
              <motion.div
                key={categoryKey}
                layout
                className="w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <CategoryCard
                  categoryKey={categoryKey}
                  categoryName={categoryName}
                  categoryDescription={description}
                  icon={IconComponent}
                  isExpanded={isExpanded}
                  onToggle={() => toggleCard(categoryKey)}
                  onValueChange={(path, value) => {
                    console.log(`🌟 PortfolioConfigCards passing categoryKey: "${categoryKey}" to CategoryCard`);
                    onValueChange(path, value);
                  }}
                  onRemoveOverride={onRemoveOverride}
                  onResetCategory={onResetCategory}
                  isUpdating={isUpdating}
                />
              </motion.div>
            );
          }

          return null; // Don't render non-expanded cards when one is expanded
        })}

        {/* Render collapsed cards in grid when no card is expanded */}
        {expandedCards.size === 0 && (
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, staggerChildren: 0.1 }}
          >
            {portfolioCategories && Object.entries(portfolioCategories).map(([categoryKey, description]) => {
              const IconComponent = categoryIcons[categoryKey] || CogIcon;
              const isExpanded = false; // All cards are collapsed in this view

              const categoryName = categoryKey.replace('_', ' ').split(' ').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');

              return (
                <motion.div
                  key={categoryKey}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <CategoryCard
                    categoryKey={categoryKey}
                    categoryName={categoryName}
                    categoryDescription={description}
                    icon={IconComponent}
                    isExpanded={isExpanded}
                    onToggle={() => toggleCard(categoryKey)}
                    onValueChange={(path, value) => {
                      console.log(`🌟 PortfolioConfigCards passing categoryKey: "${categoryKey}" to CategoryCard`);
                      onValueChange(path, value);
                    }}
                    onRemoveOverride={onRemoveOverride}
                    onResetCategory={onResetCategory}
                    isUpdating={isUpdating}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* Configuration Wizard */}
      <ConfigWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onComplete={handleWizardComplete}
      />
    </div>
  );
};
